import { action, computed, observable } from "mobx";
import TourJob from "~/models/TourJob/TourJob";
import TourJobRepository from "~/repository/TourJob/TourJobRepository";
import NestStore from "~/stores/NestStore";
import TourJobType from "~/constants/TourJob/TourJobType";

class TourJobStore extends NestStore {
  rootStore = null;

  /**
   * @property {Array}
   */
  @observable tourJobs = [];

  /**
   * @property {Object|null}
   */
  @observable tourJob = null;

  /**
   * @property {number|null}
   */
  @observable tourJobId = null;

  /**
   * @property {TourJobRepository|null}
   */
  tourJobRepository = null;

  constructor(rootStore) {
    super();

    this.rootStore = rootStore;

    this.tourJobRepository = new TourJobRepository();

    this.setDefaultSearchParams();
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([this.getOne()]).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.searchParams.providerId !== "") {
      params.providerId = this.searchParams.providerId;
    }

    if (this.searchParams.tourId !== "") {
      params.tourId = this.searchParams.tourId;
    }

    if (this.searchParams.status !== "") {
      params.status = this.searchParams.status;
    }

    return this.tourJobRepository.fetch(params).then(response => {
      this.processEntities(response.data.tourJobs);
      this.processPagination(response.data.pagination);
    });
  }

  @action
  processEntities(tourJobs = []) {
    this.tourJobs = [];

    tourJobs.forEach(tourJobArray => {
      const tourJob = new TourJob(tourJobArray);

      this.tourJobs.push(tourJob);
    });
  }

  @action
  getOne() {
    return this.tourJobRepository.fetchOne(this.tourJobId).then(response => {
      this.tourJob = new TourJob(response.data.tourJob);
    });
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  changePage(page) {
    this.pagination.currentPage = page;

    return this.loadIndex();
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  search() {
    this.pagination.currentPage = 1;

    return this.loadIndex();
  }

  /**
   * Set default search params.
   */
  @action
  setDefaultSearchParams() {
    this.searchParams.providerId = "";
    this.searchParams.tourId = "";
    this.searchParams.status = "";
  }

  @computed
  get immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob() {
    const index = this.tourJobs.findIndex(
      tourJob =>
        tourJob.typeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS ||
        tourJob.typeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN,
    );

    if (index !== -1) {
      return this.tourJobs[index];
    }

    return null;
  }

  @action
  purchaseProduct(tourId, productId) {
    this.isLoading = true;

    return this.tourJobRepository
      .purchaseProduct(tourId, productId)
      .then(() => {
        this.loadIndex();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  refundProduct(tourJobId, productId) {
    this.isLoading = true;

    return this.tourJobRepository
      .refundProduct(tourJobId, productId)
      .then(() => {
        this.rootStore.creditUsageStore.loadIndex();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}

export default TourJobStore;
